import { useState, useCallback, useEffect } from "react";
import { HiOutlineBars3, HiOutlineXMark } from "react-icons/hi2";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import Footer from "./Footer";

// Navigation config
const NAV_CONFIG = {
  student: [
    { to: "/student/dashboard", label: "Dashboard" },
    { to: "/student/bookings/new", label: "New Session" },
    { to: "/tutors", label: "Browse Tutors" },
    { to: "/student/profile", label: "Profile" },
  ],
  tutor: [
    { to: "/tutor/dashboard", label: "Dashboard" },
    { to: "/tutor/profile", label: "Profile" },
    { to: "/tutor/sessions", label: "Sessions" },
  ],
  admin: [
    { to: "/admin/dashboard", label: "Dashboard" },
    { to: "/admin/users", label: "Users" },
    { to: "/admin/subjects", label: "Subjects" },
    { to: "/admin/bookings", label: "Bookings" },
    { to: "/admin/sessions", label: "Sessions" },
    { to: "/admin/theme", label: "Themes" },
  ],
};

export default function Layout() {
  const { user, logout } = useAuth();
  const { cycleTheme, customTheme } = useTheme();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = useCallback(() => {
    logout();
    setMobileMenuOpen(false);
  }, [logout]);

  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    closeMobileMenu();
  }, [location.pathname, closeMobileMenu]);

  // Lock body scroll when menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  return (
    <div className="flex flex-col min-h-screen bg-background text-text">
      {/* Top Navigation Bar */}
      <nav className="sticky top-0 z-50 bg-background border-b border-border shadow-sm">
        <div className="flex items-center justify-between h-16 px-4 max-w-7xl mx-auto">
          {/* Logo */}
          <Link
            to="/"
            className="text-2xl font-bold text-primary hover:opacity-80 transition-opacity"
          >
            PeerTutors
          </Link>

          {/* Desktop Navigation */}
          {user && (
            <div className="hidden lg:flex items-center gap-6">
              {NAV_CONFIG[user.role]?.map(({ to, label }) => (
                <NavLink
                  key={to}
                  to={to}
                  end
                  className={({ isActive }) =>
                    `px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                      isActive
                        ? "text-primary bg-primary/10"
                        : "text-text hover:text-primary hover:bg-surface"
                    }`
                  }
                >
                  {label}
                </NavLink>
              ))}
            </div>
          )}

          {/* Right Side */}
          <div className="flex items-center gap-3">
            {/* Theme Toggle - Desktop */}
            <button
              onClick={cycleTheme}
              className="hidden sm:flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md border border-border bg-surface hover:bg-surface/80 transition-colors"
            >
              {customTheme?.name || "Default"}
            </button>

            {/* Desktop User Menu */}
            {user ? (
              <div className="hidden lg:flex items-center gap-3">
                <span className="text-sm font-medium truncate max-w-[120px]">
                  {user.name?.first}
                </span>
                <button
                  onClick={handleLogout}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-md transition-colors"
                >
                  Logout
                </button>
              </div>
            ) : (
              <div className="hidden lg:flex items-center gap-3">
                <Link
                  to="/login"
                  className="px-4 py-2 text-sm font-medium hover:text-primary transition-colors"
                >
                  Login
                </Link>
                <Link
                  to="/signup"
                  className="px-4 py-2 text-sm font-medium text-white bg-primary hover:bg-primary/90 rounded-md transition-colors"
                >
                  Sign Up
                </Link>
              </div>
            )}

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-md hover:bg-surface transition-colors"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <HiOutlineXMark className="w-6 h-6" />
              ) : (
                <HiOutlineBars3 className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-40 lg:hidden"
          onClick={closeMobileMenu}
        />
      )}

      {/* Mobile Menu Drawer */}
      <div
        className={`fixed top-0 right-0 bottom-0 w-[85vw] max-w-sm bg-background z-50 lg:hidden transform transition-transform duration-300 ease-out ${
          mobileMenuOpen ? "translate-x-0" : "translate-x-full"
        } shadow-2xl`}
      >
        <div className="flex flex-col h-full">
          {/* Mobile Menu Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-border">
            <span className="text-xl font-bold text-primary">Menu</span>
            <button
              onClick={closeMobileMenu}
              className="p-2 rounded-md hover:bg-surface"
            >
              <HiOutlineXMark className="w-6 h-6" />
            </button>
          </div>

          {/* Menu Content */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-5 space-y-6">
              {/* User Info */}
              {user && (
                <div className="pb-6 border-b border-border">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-lg font-bold text-primary">
                        {user.name?.first?.[0]}
                        {user.name?.last?.[0]}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-base truncate">
                        {user.name?.first} {user.name?.last}
                      </p>
                      <p className="text-sm text-text/60 truncate">
                        {user.email}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Navigation Links */}
              {user && (
                <div className="space-y-2">
                  {NAV_CONFIG[user.role]?.map(({ to, label }) => (
                    <NavLink
                      key={to}
                      to={to}
                      end
                      onClick={closeMobileMenu}
                      className={({ isActive }) =>
                        `block px-4 py-3 rounded-lg font-medium transition-colors ${
                          isActive
                            ? "text-primary bg-primary/10"
                            : "text-text hover:bg-surface"
                        }`
                      }
                    >
                      {label}
                    </NavLink>
                  ))}
                </div>
              )}

              {/* Theme Toggle */}
              <div className="pt-6 border-t border-border">
                <button
                  onClick={cycleTheme}
                  className="w-full flex items-center justify-between px-4 py-3 rounded-lg hover:bg-surface transition-colors"
                >
                  <span className="font-medium">Theme</span>
                  <span className="text-primary font-semibold">
                    {customTheme?.name || "Default"}
                  </span>
                </button>
              </div>
            </div>
          </div>

          {/* Bottom Actions */}
          <div className="p-5 border-t border-border">
            {user ? (
              <button
                onClick={handleLogout}
                className="w-full py-3 text-white bg-red-500 hover:bg-red-600 rounded-lg font-semibold transition-colors"
              >
                Logout
              </button>
            ) : (
              <div className="space-y-3">
                <Link
                  to="/login"
                  onClick={closeMobileMenu}
                  className="block w-full py-3 text-center border border-border rounded-lg font-semibold hover:bg-surface transition-colors"
                >
                  Login
                </Link>
                <Link
                  to="/signup"
                  onClick={closeMobileMenu}
                  className="block w-full py-3 text-center text-white bg-primary hover:bg-primary/90 rounded-lg font-semibold transition-colors"
                >
                  Sign Up
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="flex-1">
        <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
          <Outlet />
        </div>
      </main>

      <Footer />
    </div>
  );
}
